import { isGapSuggestionKind } from "@repo/shared/findingPlanning";
import {
  isOpenFindingStatus,
  isOpenSuggestionStatus,
  openGapCount,
  openReviewCount,
  openSuggestionCount,
  pendingRecordUpdateCount,
} from "@repo/shared/reviewCounts";
/**
 * The Regulation landing page, in three bands: a stat strip (the four numbers
 * a compliance officer opens the day with), two charts (the state of the
 * library, and how the work is moving), then the documents most recently
 * touched.
 *
 * Every figure comes from rows that exist. A ratio with no denominator renders
 * an em dash rather than 0%, and an empty library renders an invitation rather
 * than a zeroed dashboard — in this product a confidently wrong number is
 * worse than an absent one.
 *
 * The composition follows a dissection of a reference dashboard the client
 * admired, translated to this light theme: one segmented stat strip rather
 * than four loose boxes, 30px/700 tabular numerals as the loudest thing on the
 * page, a 3-tier surface stack (canvas → card → well), a 160px donut with a
 * centre stat and a right-aligned legend, bars with a highlight well beside
 * them, and a real table with columns instead of a file list. Chart colour is
 * the workspace's status tokens, so every theme paints its own charts;
 * identity never rides on colour alone — every slice is named and counted.
 *
 * @module components/regulation/OverviewSection
 */
import { DONUT_SEGMENT_PROPS, DONUT_TOOLTIP_PROPS, GovernancePieSector } from "./GovernancePieSector";
import {
  ArrowRightIcon,
  ChartColumnIcon,
  ChartPieIcon,
  FileTextIcon,
  LightbulbIcon,
  RadarIcon,
  ScrollTextIcon,
  ShieldCheckIcon,
} from "lucide-react";
import { useMemo, type ComponentType, type ReactNode } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { cn } from "~/lib/utils";
import { policyDisplayName, type Policy } from "../../state/policies";
import type {
  OrganizationPerson,
  OrgPolicyGovernance,
  Recommendation,
  RegulationOverview,
  TimelineBucket,
} from "../../state/governance";
import { ChartTooltipContent } from "../ui/chart";
import { OverviewSkeleton } from "./LoadingSkeletons";
import { PersonAvatars } from "./PersonAvatars";
import { IssuesChart } from "./IssuesChart";
import {
  FRESHNESS_LABELS,
  FRESHNESS_PILL,
  freshnessSlices,
  policyFreshness,
  type Freshness,
} from "./policy/policyFreshness";
import { OpenWorkBadges } from "./policy/OpenWorkBadges";
import { NO_OPEN_WORK, openFindingsByPolicy, openWorkByPolicy } from "./policy/openWorkByPolicy";

/* ------------------------------------------------------------------ */
/* What the page is told                                               */
/* ------------------------------------------------------------------ */

/** A regulatory finding, as much of it as the overview needs. */
export interface OverviewFinding {
  readonly policyId: string;
  readonly title: string;
  readonly status: string;
  readonly severity: string | null;
  readonly detectedAt: string;
}

export interface OverviewFacts {
  readonly reviewCounts?: import("@repo/shared/reviewCounts").ReviewCounts;
  readonly overview: RegulationOverview;
  readonly policies: ReadonlyArray<Policy>;
  readonly governance: ReadonlyMap<string, OrgPolicyGovernance>;
  readonly owners: ReadonlyMap<string, ReadonlyArray<OrganizationPerson>>;
  readonly recommendations: ReadonlyArray<Recommendation>;
  readonly findings: ReadonlyArray<OverviewFinding>;
  readonly touchedPolicyIds: ReadonlySet<string>;
  readonly timeline: ReadonlyArray<TimelineBucket>;
  /** One clock reading per render so every figure agrees on "now". */
  readonly now: number;
}

const DAY = 24 * 60 * 60 * 1000;
const DUE_SOON_DAYS = 30;
const MONTHS = 6;

const isUrgent = (severity: string | null) => severity === "high" || severity === "critical";

/** Open findings and suggestions per document, from the overview's facts. */
export const overviewOpenWork = (facts: OverviewFacts) => openWorkByPolicy(facts.findings, facts.recommendations);

/** The library's freshness, counting a document with an open finding as not up to date. */
export const overviewFreshnessSlices = (facts: OverviewFacts) =>
  freshnessSlices(
    facts.policies,
    facts.governance,
    facts.touchedPolicyIds,
    facts.now,
    openFindingsByPolicy(overviewOpenWork(facts)),
  );

/** The counts every band shares, computed once. */
export function overviewNumbers(facts: OverviewFacts) {
  const openRecommendations = facts.recommendations.filter((item) => isOpenSuggestionStatus(item.status));
  const openFindings = facts.findings.filter((item) => isOpenFindingStatus(item.status));
  const urgent =
    openRecommendations.filter((item) => isUrgent(item.severity)).length +
    openFindings.filter((item) => isUrgent(item.severity)).length;
  let overdue = 0;
  let dueSoon = 0;
  let governed = 0;
  for (const policy of facts.policies) {
    const row = facts.governance.get(policy.id);
    const hasOwners = (facts.owners.get(policy.id)?.length ?? 0) > 0;
    if (row !== undefined || hasOwners) governed += 1;
    const due = row?.reviewDueAt ? Date.parse(row.reviewDueAt) : Number.NaN;
    if (Number.isNaN(due)) continue;
    if (due < facts.now) overdue += 1;
    else if (due - facts.now <= DUE_SOON_DAYS * DAY) dueSoon += 1;
  }
  // Split the way the sidebar splits it: gap suggestions (control amendments,
  // missing documents) are findings; the rest are suggestions. Counting them
  // as suggestions here said "53 suggestions · 9 findings" beside a sidebar
  // reading 34 findings and 28 suggestions.
  // Both halves always add up to `waiting`. Live counts from an older server
  // carry no gap split; they keep the plain queue split rather than a guess.
  const gapSuggestions = openRecommendations.filter((item) => isGapSuggestionKind(item.kind)).length;
  const live = facts.reviewCounts;
  const liveFindings = live ? (openGapCount(live) ?? live.findings.open) : null;
  const liveSuggestions = live ? (pendingRecordUpdateCount(live) ?? openSuggestionCount(live)) : null;
  return {
    openRecommendations,
    openFindings,
    suggestionsWaiting: liveSuggestions ?? openRecommendations.length - gapSuggestions,
    findingsWaiting: liveFindings ?? openFindings.length + gapSuggestions,
    waiting: facts.reviewCounts ? openReviewCount(facts.reviewCounts) : openRecommendations.length + openFindings.length,
    urgent,
    overdue,
    dueSoon,
    governed,
  };
}

/* ------------------------------------------------------------------ */
/* Shared pieces                                                       */
/* ------------------------------------------------------------------ */

/** A card: the second surface, on the page's canvas. */
const CARD = "rounded-2xl border border-border/60 bg-card shadow-[0_1px_2px_rgba(15,23,42,0.04)]";
/**
 * A well inside a card: the third surface. This theme's `muted` is a hair
 * off white, so a well needs its hairline to read as a container at all.
 */
const WELL = "rounded-xl border border-border/60 bg-muted";

function IconTile({ icon: Icon }: { readonly icon: ComponentType<{ className?: string }> }) {
  return (
    <span className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-muted text-muted-foreground">
      <Icon className="size-4" />
    </span>
  );
}

function CardHeader({
  icon,
  title,
  aside,
}: {
  readonly icon: ComponentType<{ className?: string }>;
  readonly title: string;
  readonly aside?: ReactNode | undefined;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="flex items-center gap-2.5">
        <IconTile icon={icon} />
        <h2 className="text-[15px] font-semibold text-foreground">{title}</h2>
      </span>
      {aside}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Stat strip                                                          */
/* ------------------------------------------------------------------ */

type Tone = "neutral" | "good" | "warn" | "bad";

const TONE: Record<Tone, string> = {
  neutral: "text-muted-foreground",
  good: "text-success",
  warn: "text-warning",
  bad: "text-destructive",
};

function Stat({
  icon,
  label,
  value,
  delta,
  deltaTone = "neutral",
  detail,
  detailTitle,
  onOpen,
}: {
  readonly icon: ComponentType<{ className?: string }>;
  readonly label: string;
  readonly value: string;
  /** The part of the bottom line that carries meaning — coloured. */
  readonly delta?: string | undefined;
  readonly deltaTone?: Tone | undefined;
  /** The rest of the bottom line — muted. May hold its own links (above the tile's). */
  readonly detail: ReactNode;
  /** The hover text when `detail` is not plain text. */
  readonly detailTitle?: string | undefined;
  /** Makes the whole tile open its list. */
  readonly onOpen?: (() => void) | undefined;
}) {
  const plain = typeof detail === "string" ? detail : (detailTitle ?? "");
  return (
    <div
      className={cn(
        "relative flex min-w-0 flex-col gap-3 px-5 py-5",
        onOpen && "transition-colors hover:bg-foreground/[0.025] has-[[data-stat-open]:focus-visible]:bg-foreground/[0.04]",
      )}
    >
      {onOpen ? (
        <button
          type="button"
          data-stat-open=""
          onClick={onOpen}
          className="flex cursor-pointer items-center gap-2.5 text-start text-xs font-medium text-muted-foreground outline-none after:absolute after:inset-0"
        >
          <IconTile icon={icon} />
          {label}
        </button>
      ) : (
        <span className="flex items-center gap-2.5 text-xs font-medium text-muted-foreground">
          <IconTile icon={icon} />
          {label}
        </span>
      )}
      <span className="text-[30px] font-bold leading-9 tabular-nums tracking-tight text-foreground">
        {value}
      </span>
      {/* One line, always: a tile that wraps is taller than its neighbours
          and the strip stops reading as a strip. Copy is kept short enough
          to fit; the ellipsis is the last resort, with the full text on
          hover. */}
      <span
        className="truncate text-xs leading-4 text-muted-foreground"
        title={delta ? `${delta} ${plain}` : plain}
      >
        {delta ? <span className={cn("font-semibold", TONE[deltaTone])}>{delta} </span> : null}
        {detail}
      </span>
    </div>
  );
}

/** A count in a tile's bottom line that opens its own queue, above the tile's link. */
function StatLink({ onOpen, children }: { readonly onOpen: () => void; readonly children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="relative z-10 cursor-pointer underline-offset-2 hover:text-foreground hover:underline focus-visible:underline focus-visible:outline-none"
    >
      {children}
    </button>
  );
}

export type DecisionQueue = "findings" | "suggestions";

function StatStrip({
  facts,
  onOpenQueue,
}: {
  readonly facts: OverviewFacts;
  readonly onOpenQueue?: ((queue: DecisionQueue) => void) | undefined;
}) {
  const numbers = overviewNumbers(facts);
  const { documents } = facts.overview;
  const total = facts.policies.length;
  return (
    <div
      className={cn(
        CARD,
        "grid divide-y divide-border/60 sm:grid-cols-2 sm:divide-x lg:col-span-12 lg:grid-cols-4 lg:divide-y-0",
      )}
    >
      <Stat
        icon={ScrollTextIcon}
        label="Policies governed"
        value={total === 0 ? "—" : String(numbers.governed)}
        delta={total === 0 ? undefined : `${Math.round((numbers.governed / total) * 100)}%`}
        deltaTone={total === 0 ? "neutral" : numbers.governed === total ? "good" : "warn"}
        detail={total === 0 ? "nothing in the library yet" : `of ${total} in the library`}
      />
      <Stat
        icon={LightbulbIcon}
        label="Waiting on a decision"
        value={String(numbers.waiting)}
        delta={numbers.urgent > 0 ? `${numbers.urgent} high` : undefined}
        deltaTone={numbers.urgent > 0 ? "bad" : "neutral"}
        onOpen={onOpenQueue ? () => onOpenQueue("findings") : undefined}
        detailTitle={`${numbers.suggestionsWaiting} suggestions · ${numbers.findingsWaiting} findings`}
        detail={
          numbers.waiting === 0 ? (
            "no findings or suggestions open"
          ) : onOpenQueue ? (
            <>
              <StatLink onOpen={() => onOpenQueue("suggestions")}>
                {numbers.suggestionsWaiting} suggestions
              </StatLink>
              {" · "}
              <StatLink onOpen={() => onOpenQueue("findings")}>
                {numbers.findingsWaiting} findings
              </StatLink>
            </>
          ) : (
            `${numbers.suggestionsWaiting} suggestions · ${numbers.findingsWaiting} findings`
          )
        }
      />
      <Stat
        icon={RadarIcon}
        label="Reviews"
        value={String(numbers.overdue)}
        delta={numbers.overdue > 0 ? "overdue" : numbers.dueSoon > 0 ? "on schedule" : undefined}
        deltaTone={numbers.overdue > 0 ? "bad" : "good"}
        detail={
          numbers.overdue > 0
            ? `${numbers.dueSoon} more due in ${DUE_SOON_DAYS} days`
            : numbers.dueSoon > 0
              ? `${numbers.dueSoon} due in the next ${DUE_SOON_DAYS} days`
              : "nothing due in the next 30 days"
        }
      />
      <Stat
        icon={FileTextIcon}
        label="Documents"
        value={documents.total === 0 ? "—" : String(documents.ready)}
        delta={
          documents.total > 0 && documents.ready < documents.total
            ? `${documents.total - documents.ready} pending`
            : undefined
        }
        deltaTone="warn"
        detail={documents.total === 0 ? "none ingested yet" : `of ${documents.total} ready for retrieval`}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Review health donut                                                 */
/* ------------------------------------------------------------------ */

function ReviewHealthCard({ facts }: { readonly facts: OverviewFacts }) {
  const slices = useMemo(() => overviewFreshnessSlices(facts), [facts]);
  const current = slices.find((slice) => slice.key === "current")?.count ?? 0;
  return (
    <section className={cn(CARD, "flex min-w-0 flex-col p-5 lg:col-span-5")}>
      <CardHeader
        icon={ChartPieIcon}
        title="Review health"
        aside={
          <span className="text-xs text-muted-foreground">
            {facts.policies.length === 0 ? "" : `${current} of ${facts.policies.length} up to date`}
          </span>
        }
      />
      {facts.policies.length === 0 ? (
        <p className="my-auto py-10 text-center text-sm text-muted-foreground">
          Upload a policy and its review health appears here.
        </p>
      ) : (
        <div className="mt-5 flex flex-1 items-center gap-6">
          <div className="relative size-40 shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  shape={GovernancePieSector}
                  data={[...slices]}
                  dataKey="count"
                  nameKey="label"
                  innerRadius="72%"
                  outerRadius="100%"
                  cornerRadius={6}
                  {...DONUT_SEGMENT_PROPS}
                >
                  {slices.map((slice) => (
                    <Cell key={slice.key} fill={slice.color} />
                  ))}
                </Pie>
                <Tooltip {...DONUT_TOOLTIP_PROPS} content={<ChartTooltipContent hideLabel />} />
              </PieChart>
            </ResponsiveContainer>
            <span className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-[22px] font-bold leading-7 tabular-nums tracking-tight text-foreground">
                {facts.policies.length}
              </span>
              <span className="text-[11px] font-medium text-muted-foreground">documents</span>
            </span>
          </div>
          <ul className="min-w-0 flex-1 space-y-2.5">
            {slices.map((slice) => (
              <li key={slice.key} className="flex items-center gap-2.5 text-[13px]">
                <span
                  aria-hidden
                  className="h-3 w-[3px] shrink-0 rounded-full"
                  style={{ background: slice.color }}
                />
                <span className="min-w-0 flex-1 truncate font-medium text-foreground">
                  {slice.label}
                </span>
                <span className="font-bold tabular-nums text-foreground">{slice.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Work over six months                                                */
/* ------------------------------------------------------------------ */

const SERIES = [
  { key: "raised", label: "Raised", color: "var(--color-primary)" },
  { key: "closed", label: "Closed", color: "var(--color-success)" },
] as const;

const monthKey = (iso: string): string => iso.slice(0, 7);

const monthLabel = (isoMonth: string): string => {
  const parsed = new Date(`${isoMonth}-01T00:00:00`);
  return Number.isNaN(parsed.getTime())
    ? isoMonth
    : parsed.toLocaleDateString(undefined, { month: "short" });
};

/** The last six months, oldest first, as `YYYY-MM`. Always six — an empty month is a bar at zero, not a missing month. */
function lastMonths(now: number): string[] {
  const out: string[] = [];
  const cursor = new Date(now);
  cursor.setDate(1);
  for (let index = MONTHS - 1; index >= 0; index -= 1) {
    const d = new Date(cursor.getFullYear(), cursor.getMonth() - index, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}

/**
 * What was raised and what was closed, month by month: regulatory findings
 * and recommendations raised, against recommendations decided and policies
 * amended. The pairing is the point — a month of raising with no closing is
 * the backlog to talk about.
 */
export function workBuckets(facts: OverviewFacts) {
  const months = lastMonths(facts.now);
  const raised = new Map(months.map((month) => [month, 0]));
  const closed = new Map(months.map((month) => [month, 0]));
  const bump = (map: Map<string, number>, iso: string | null | undefined) => {
    if (!iso) return;
    const key = monthKey(iso);
    if (map.has(key)) map.set(key, (map.get(key) ?? 0) + 1);
  };
  for (const item of facts.recommendations) {
    bump(raised, item.createdAt);
    if (item.status === "applied" || item.status === "dismissed") bump(closed, item.decidedAt);
  }
  for (const item of facts.findings) bump(raised, item.detectedAt);
  for (const bucket of facts.timeline) {
    if (closed.has(bucket.month)) closed.set(bucket.month, (closed.get(bucket.month) ?? 0) + bucket.amendments);
  }
  return months.map((month) => ({
    month,
    label: monthLabel(month),
    raised: raised.get(month) ?? 0,
    closed: closed.get(month) ?? 0,
  }));
}

function WorkCard({ facts }: { readonly facts: OverviewFacts }) {
  const data = useMemo(() => workBuckets(facts), [facts]);
  const numbers = overviewNumbers(facts);
  // The most pressing open item, recommendation or finding: urgent first,
  // then the most recently raised. What this says must be true of both
  // kinds — "everything decided" while a finding sits open is a lie.
  const pressing = [
    ...numbers.openRecommendations.map((item) => ({
      title: item.title,
      severity: item.severity,
      raisedAt: item.createdAt,
    })),
    ...numbers.openFindings.map((item) => ({
      title: item.title,
      severity: item.severity,
      raisedAt: item.detectedAt,
    })),
  ]
    .sort(
      (a, b) =>
        Number(isUrgent(b.severity)) - Number(isUrgent(a.severity)) ||
        Date.parse(b.raisedAt) - Date.parse(a.raisedAt),
    )
    .at(0);
  const raisedTotal = data.reduce((sum, bucket) => sum + bucket.raised, 0);
  return (
    <section className={cn(CARD, "flex min-w-0 flex-col p-5 lg:col-span-7")}>
      <CardHeader
        icon={ChartColumnIcon}
        title="Findings & recommendations"
        aside={
          <span className="flex items-center gap-3">
            {SERIES.map((series) => (
              <span key={series.key} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span aria-hidden className="size-2.5 rounded-full" style={{ background: series.color }} />
                {series.label}
              </span>
            ))}
          </span>
        }
      />
      <div className="mt-5 grid flex-1 gap-5 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <div className={cn(WELL, "flex flex-col justify-between p-4")}>
          <div>
            <span className="text-[22px] font-bold leading-7 tabular-nums tracking-tight text-foreground">
              {numbers.waiting}
            </span>
            <p className="mt-0.5 text-xs text-muted-foreground">
              open now
              {numbers.urgent > 0 ? (
                <>
                  {" · "}
                  <span className="font-semibold text-destructive">{numbers.urgent} high</span>
                </>
              ) : null}
            </p>
          </div>
          <p className="mt-4 text-xs leading-5 text-muted-foreground">
            {pressing ? (
              <>
                <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Most pressing
                </span>
                <span className="line-clamp-4 text-foreground">{pressing.title}</span>
              </>
            ) : raisedTotal === 0 ? (
              "Nothing raised in the last six months. The weekly inspection reports here."
            ) : (
              "Nothing open — everything raised has been decided."
            )}
          </p>
        </div>
        <div className="h-44 min-w-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 4, right: 0, bottom: 0, left: -22 }} barGap={4}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11, fill: "var(--color-muted-foreground)" }}
              />
              <YAxis
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11, fill: "var(--color-muted-foreground)" }}
              />
              <Tooltip
                cursor={{ fill: "var(--color-muted)", opacity: 0.5 }}
                wrapperStyle={{ zIndex: 40 }}
                content={<ChartTooltipContent />}
              />
              {SERIES.map((series) => (
                <Bar
                  key={series.key}
                  name={series.label}
                  dataKey={series.key}
                  fill={series.color}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={18}
                  isAnimationActive={false}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Recent documents                                                    */
/* ------------------------------------------------------------------ */

const formatDate = (value: string): string => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? "—"
    : parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const HEAD = "px-4 py-2.5 text-xs font-medium text-muted-foreground";

function RecentDocumentsCard({
  facts,
  onOpenAll,
  onOpenPolicy,
}: {
  readonly facts: OverviewFacts;
  readonly onOpenAll: () => void;
  readonly onOpenPolicy: (policyId: string) => void;
}) {
  const recent = facts.policies.slice(0, 6);
  // Findings (red) and suggestions (neutral) apart, as on the policies shelf.
  const openByPolicy = useMemo(() => overviewOpenWork(facts), [facts]);

  return (
    <section className={cn(CARD, "min-w-0 lg:col-span-12")}>
      <header className="flex items-center justify-between gap-3 px-5 py-4">
        <CardHeader
          icon={FileTextIcon}
          title="Recent documents"
          aside={
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
              {facts.policies.length}
            </span>
          }
        />
        <button
          type="button"
          onClick={onOpenAll}
          className="flex cursor-pointer items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          All policies
          <ArrowRightIcon className="size-3.5" />
        </button>
      </header>
      {recent.length === 0 ? (
        <p className="border-t border-border/60 px-5 py-10 text-center text-sm text-muted-foreground">
          Documents you upload appear here, newest first.
        </p>
      ) : (
        <div className="overflow-x-auto border-t border-border/60">
          <table className="w-full min-w-[820px] text-sm">
            <thead>
              <tr className="border-b border-border/60 text-left">
                <th className={HEAD}>Document</th>
                <th className={HEAD}>Owner</th>
                <th className={HEAD}>Department</th>
                <th className={HEAD}>Review</th>
                <th className={cn(HEAD, "text-right")}>Open</th>
                <th className={cn(HEAD, "text-right")}>Updated</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((policy) => {
                const freshness: Freshness = policyFreshness({
                  policy,
                  governance: facts.governance.get(policy.id) ?? null,
                  touchedByChange: facts.touchedPolicyIds.has(policy.id),
                  openFindings: (openByPolicy.get(policy.id) ?? NO_OPEN_WORK).findings,
                  now: facts.now,
                });
                const open = openByPolicy.get(policy.id) ?? NO_OPEN_WORK;
                return (
                  <tr
                    key={policy.id}
                    onClick={() => onOpenPolicy(policy.id)}
                    className="h-[52px] cursor-pointer border-b border-border/60 transition-colors last:border-b-0 hover:bg-muted/40"
                  >
                    {/* w-full + max-w-0 hands the leftover width to this cell
                        and lets the truncate inside actually bite. */}
                    <td className="w-full max-w-0 px-4 py-2">
                      <span className="flex min-w-0 items-center gap-3">
                        <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                          <FileTextIcon className="size-3.5" />
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate text-[13px] font-semibold text-foreground">
                            {policyDisplayName(policy)}
                          </span>
                          {policy.title?.trim() ? (
                            <span className="block truncate text-[11px] text-muted-foreground">
                              {policy.filename}
                            </span>
                          ) : null}
                        </span>
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <PersonAvatars people={facts.owners.get(policy.id) ?? []} emptyLabel="Unassigned" />
                    </td>
                    <td className="max-w-[10rem] truncate whitespace-nowrap px-4 py-2 text-xs text-muted-foreground">
                      {facts.governance.get(policy.id)?.department ?? "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2">
                      <span
                        className={cn(
                          "rounded-full px-2 py-0.5 text-[11px] font-medium",
                          FRESHNESS_PILL[freshness],
                        )}
                      >
                        {FRESHNESS_LABELS[freshness]}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      <OpenWorkBadges work={open} emptyLabel="—" className="ml-auto" />
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-right text-xs tabular-nums text-muted-foreground">
                      {formatDate(policy.createdAt)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* The page                                                            */
/* ------------------------------------------------------------------ */

export function OverviewSection({
  facts,
  isPending,
  onOpenAll,
  onOpenPolicy,
  onOpenQueue,
  inspectionActivity,
}: {
  readonly facts: OverviewFacts | null;
  readonly isPending: boolean;
  readonly onOpenAll: () => void;
  readonly onOpenPolicy: (policyId: string) => void;
  /** "Waiting on a decision" opens Findings; its two counts open their own queues. */
  readonly onOpenQueue?: ((queue: DecisionQueue) => void) | undefined;
  readonly inspectionActivity?: ReactNode;
}) {
  if (facts === null) {
    if (isPending) {
      return <OverviewSkeleton />;
    }
    return (
      <div className="rounded-2xl border border-dashed border-border/60 px-6 py-16 text-center">
        <p className="text-sm text-muted-foreground">Nothing to show yet.</p>
      </div>
    );
  }

  return (
    <div className="grid min-w-0 grid-cols-1 gap-5 lg:grid-cols-12">
      <StatStrip facts={facts} onOpenQueue={onOpenQueue} />
      <ReviewHealthCard facts={facts} />
      <WorkCard facts={facts} />
      {inspectionActivity ? <div data-slot="overview-activity-row" className="grid min-w-0 auto-rows-[20rem] grid-cols-1 gap-5 lg:col-span-12 lg:grid-cols-12">
        {inspectionActivity}
        <IssuesChart counts={facts.reviewCounts} findings={facts.findings} recommendations={facts.recommendations} className="lg:col-span-5" />
      </div> : null}
      <RecentDocumentsCard facts={facts} onOpenAll={onOpenAll} onOpenPolicy={onOpenPolicy} />
    </div>
  );
}

/** The one line above the page: what wants attention, in the order it matters. */
export function attentionSummary(facts: OverviewFacts): ReactNode {
  const numbers = overviewNumbers(facts);
  const failed = facts.policies.filter((policy) => policy.status === "failed").length;
  const parts: ReactNode[] = [];
  const strong = (text: string) => (
    <span key={text} className="font-semibold text-foreground">
      {text}
    </span>
  );
  if (failed > 0) parts.push(strong(`${failed} failed ${failed === 1 ? "import" : "imports"}`));
  if (numbers.overdue > 0)
    parts.push(strong(`${numbers.overdue} ${numbers.overdue === 1 ? "review" : "reviews"} overdue`));
  if (numbers.waiting > 0)
    parts.push(
      strong(
        `${numbers.waiting} ${numbers.waiting === 1 ? "decision" : "decisions"} waiting${numbers.urgent > 0 ? ` (${numbers.urgent} high)` : ""}`,
      ),
    );
  if (numbers.dueSoon > 0)
    parts.push(strong(`${numbers.dueSoon} ${numbers.dueSoon === 1 ? "review" : "reviews"} due in 30 days`));
  if (parts.length === 0) {
    return (
      <span className="flex items-center gap-1.5">
        <ShieldCheckIcon className="size-4 text-success" />
        Nothing needs your attention right now.
      </span>
    );
  }
  return (
    <>
      Today you have{" "}
      {parts.map((part, index) => (
        <span key={index}>
          {index > 0 ? (index === parts.length - 1 ? " and " : ", ") : ""}
          {part}
        </span>
      ))}
      .
    </>
  );
}
